import { injectIntl, intlShape } from '@edx/frontend-platform/i18n'
import { useMemo } from 'react'
import CourseDropdownCard from '../../components/CourseDropdownCard'
import CourseExploreCard from '../../components/CourseExploreCard'
import { withParams } from '@blend-ed/blendx-platform/utils'
import { getConfig } from '@edx/frontend-platform'

import useHandleCart from '../../utils/useHandleCart'
import TopPrograms from './top-programs'
import { useHeaderTitle } from '@blend-ed/blendx-platform'
import messages from './messages'

import { useProgramById, useEnrolledPrograms } from '../../hooks/programs/useFetchPrograms'

function ProgramSingle(props) {

    const { LMS_BASE_URL } = getConfig()
    const { params, intl } = props
    const { program: programById, loading: programByIdLoading } = useProgramById(params.id)
    const { enrolledPrograms } = useEnrolledPrograms()

    const { handleProgramsCart, programsInCart, programsInWishlist } = useHandleCart()
    const { setTitle } = useHeaderTitle()
    const alreadyEnrolled = useMemo(() => enrolledPrograms.some(program => program.program.id === params.id), [enrolledPrograms, params.id])

    // Replace useState with useMemo for programData
    const programData = useMemo(() => {
        if (programByIdLoading || !programById) return {
            title: '',
            description: '',
            image: '',
            about: '',
            estimatedTime: '',
            price: '',
            durationMetric: '',
            courses: [],
            inviteOnly: false,
            achievements: null,
            published: false
        }

        setTitle(programById.name)

        // Sort courses by displayName using natural sort for numeric values
        const sortedCourses = [...programById.programCourses].sort((a, b) => {
            const nameA = a.course.details.displayName;
            const nameB = b.course.details.displayName;

            // Extract any numeric parts for natural sort
            return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
        });

        return {
            title: programById.name,
            description: programById.description,
            about: programById.about,
            image: programById.image,
            duration: programById.duration,
            price: programById.price,
            durationMetric: programById.durationMetric.charAt(0).toUpperCase() + programById.durationMetric.slice(1),
            courses: sortedCourses,
            inviteOnly: programById.inviteOnly,
            achievements: programById.achievements,
            published: programById.published,
            tags: programById.programTags
        }
    }, [programById])

    const handleCart = () => {
        if (programsInCart.includes(params.id)) {
            handleProgramsCart(params.id, 'delete', '')
        } else {
            handleProgramsCart(params.id, 'insert', 'draft')
        }
    }

    const handleWishlist = () => {
        if (programsInWishlist.includes(params.id)) {
            handleProgramsCart(params.id, 'delete', '')
        } else {
            handleProgramsCart(params.id, 'insert', 'wishlist')
        }
    }

    return (
        <main className='program-single__wrapper'>
            <div className="program-single__left">
                <div className="program-single__description-wrapper">
                    <div className='program-single__description-about'>
                        <span className='program-single__description-title'>{intl.formatMessage(messages.description)}</span>
                        {!programByIdLoading && <p className='program-single__description-text'>{programData.description ? programData.description : intl.formatMessage(messages.noDescriptionFound)}</p>}
                    </div>
                    <div className='program-single__description-about'>
                        <span className='program-single__description-title'>{intl.formatMessage(messages.about)}</span>
                        {!programByIdLoading && <p className='program-single__description-text'>{programData.about ? programData.about : intl.formatMessage(messages.noAboutFound)}</p>}
                    </div>

                    {!programByIdLoading && programData.courses.length > 0 && programData.courses?.[0].course ?
                        <div className="program-single__content">
                            <h3 className='program-single__description-title'>{intl.formatMessage(messages.contents)}</h3>
                            <div className="courses-section">
                                {programData.courses.map((course, index) => (
                                    <CourseDropdownCard
                                        key={index}
                                        img={`${LMS_BASE_URL}${course.course.details.courseImageUrl}`}
                                        title={course.course.details.displayName}
                                        description={course.course.details.description}
                                        link={`/dashboard/course/${course.course.openedxCourseId}`}
                                    />
                                ))}
                            </div>
                        </div> : <div className="courses-section">
                            <p>{intl.formatMessage(messages.noCoursesFound)}</p>
                        </div>
                    }
                    <TopPrograms curr_program_id={params.id} />
                </div>
            </div>
            <div className="program-single__right">
                <CourseExploreCard
                    preview={programData.image}
                    price={programData.price}
                    estimatedTime={`${programData.duration} ${programData.durationMetric}`}
                    cardType="program"
                    courseCount={`${programData.courses.length} Course`}
                    primaryOnClick={() => handleCart()}
                    primaryBtnText={programsInCart.includes(params.id) ? intl.formatMessage(messages.removeFromCart) : intl.formatMessage(messages.addToCart)}
                    secondaryOnClick={() => handleWishlist()}
                    secondaryBtnText={programsInWishlist.includes(params.id) ? intl.formatMessage(messages.removeFromWishlist) : intl.formatMessage(messages.addToWishlist)}
                    inviteOnly={programData.inviteOnly}
                    alreadyEnrolled={alreadyEnrolled}
                    achievements={programData.achievements}
                    onDiscoverPage={true}
                    published={programData.published}
                    programTags={programData.tags}
                />
            </div>
        </main >
    )
}

ProgramSingle.propTypes = {
    intl: intlShape.isRequired,
};

export default injectIntl(withParams(ProgramSingle));